import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UsersIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { teamAPI } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

// Team Member Card Component
function TeamMemberCard({ member, onEdit, onDelete, onViewWorkload, onViewProjects }) {
  return (
    <div className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-semibold text-lg">
              {member.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
            <p className="text-sm text-gray-600">{member.role}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(member)}
            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
            title="Edit"
          >
            <PencilIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => onDelete(member)}
            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
            title="Delete"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center text-gray-600">
          <span className="font-medium w-24">Email:</span>
          <span>{member.email}</span>
        </div>
        <div className="flex items-center text-gray-600">
          <span className="font-medium w-24">Department:</span>
          <span>{member.department || 'N/A'}</span>
        </div>
        <div className="flex items-center text-gray-600">
          <span className="font-medium w-24">Availability:</span>
          <span>{member.availability || '100'}%</span>
        </div>
        {member.hourly_rate && (
          <div className="flex items-center text-gray-600">
            <span className="font-medium w-24">Rate:</span>
            <span>£{member.hourly_rate}/hr</span>
          </div>
        )}
        {member.skills && member.skills.length > 0 && (
          <div className="mt-3">
            <span className="font-medium text-gray-700">Skills:</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {member.skills.map((skill, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-4 pt-4 border-t border-gray-200 flex gap-2">
        <button
          onClick={() => onViewWorkload(member)}
          className="flex-1 px-3 py-2 bg-blue-50 text-blue-700 text-sm font-medium rounded-lg hover:bg-blue-100 transition-colors"
        >
          View Workload
        </button>
        <button
          onClick={() => onViewProjects(member)}
          className="flex-1 px-3 py-2 bg-green-50 text-green-700 text-sm font-medium rounded-lg hover:bg-green-100 transition-colors"
        >
          View Projects
        </button>
      </div>
    </div>
  );
}

// Add/Edit Team Member Modal
function TeamMemberModal({ member, onClose, onSave }) {
  const [formData, setFormData] = useState(member || {
    name: '',
    email: '',
    role: '',
    department: '',
    availability: '100',
    hourly_rate: '',
    skills: []
  });
  const [skillInput, setSkillInput] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const addSkill = () => {
    if (skillInput.trim()) {
      setFormData({
        ...formData,
        skills: [...(formData.skills || []), skillInput.trim()]
      });
      setSkillInput('');
    }
  };

  const removeSkill = (index) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter((_, idx) => idx !== index)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {member ? 'Edit Team Member' : 'Add Team Member'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role *
                </label>
                <input
                  type="text"
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Developer, Designer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department
                </label>
                <input
                  type="text"
                  value={formData.department || ''}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Engineering"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Availability (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.availability || '100'}
                  onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hourly Rate (£)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.hourly_rate || ''}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Skills
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Add a skill and press Enter"
                />
                <button
                  type="button"
                  onClick={addSkill}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(formData.skills || []).map((skill, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full flex items-center gap-2"
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => removeSkill(idx)}
                      className="text-blue-500 hover:text-blue-700"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                {member ? 'Update' : 'Add'} Team Member
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Workload Modal Component
function WorkloadModal({ member, onClose }) {
  const { data: workload, isLoading } = useQuery({
    queryKey: ['team-workload', member?.member_id],
    queryFn: async () => {
      const { teamAPI } = await import('../services/api');
      return teamAPI.getTeamMemberWorkload(member.member_id);
    },
    enabled: !!member,
  });

  if (!member) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {member.name}'s Workload
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Workload Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Total Tasks</p>
                  <p className="text-2xl font-bold text-blue-600">{workload?.total_tasks || 0}</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">In Progress</p>
                  <p className="text-2xl font-bold text-yellow-600">{workload?.in_progress || 0}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{workload?.completed || 0}</p>
                </div>
              </div>

              {/* Workload Percentage */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Workload Capacity</span>
                  <span className="text-sm font-bold text-gray-900">{workload?.workload_percentage || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className={`h-4 rounded-full ${(workload?.workload_percentage || 0) > 90 ? 'bg-red-500' :
                        (workload?.workload_percentage || 0) > 70 ? 'bg-yellow-500' :
                          'bg-green-500'
                      }`}
                    style={{ width: `${Math.min(workload?.workload_percentage || 0, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {(workload?.workload_percentage || 0) > 90 ? '⚠️ Overloaded' :
                    (workload?.workload_percentage || 0) > 70 ? '⚡ High Load' :
                      '✓ Healthy Load'}
                </p>
              </div>

              {/* Estimated Hours */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Time Allocation</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Hours</span>
                    <span className="font-medium">{workload?.estimated_hours || 0}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Hours Remaining</span>
                    <span className="font-medium">{workload?.hours_remaining || 0}h</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Projects Modal Component
function ProjectsModal({ member, onClose }) {
  const { data: projects, isLoading } = useQuery({
    queryKey: ['team-projects', member?.member_id],
    queryFn: async () => {
      const { teamAPI } = await import('../services/api');
      return teamAPI.getTeamMemberProjects(member.member_id);
    },
    enabled: !!member,
  });

  if (!member) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {member.name}'s Projects
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-4">
              {projects && projects.length > 0 ? (
                projects.map((project) => (
                  <div key={project.project_id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{project.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                        <div className="flex items-center gap-4 mt-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${project.status === 'completed' ? 'bg-green-100 text-green-800' :
                              project.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                            }`}>
                            {project.status?.replace('_', ' ').toUpperCase()}
                          </span>
                          {project.due_date && (
                            <span className="text-sm text-gray-500">
                              Due: {new Date(project.due_date).toLocaleDateString('en-GB')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">No projects assigned</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Team() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showWorkloadModal, setShowWorkloadModal] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const queryClient = useQueryClient();

  // Fetch team members
  const { data: teamMembers, isLoading, error } = useQuery({
    queryKey: ['team'],
    queryFn: async () => {
      const response = await teamAPI.getAll();
      // Backend returns {team_members: [...]}
      return response.team_members || response || [];
    },
  });

  // Add/Update team member mutation
  const saveMutation = useMutation({
    mutationFn: async (memberData) => {
      const baseURL = process.env.REACT_APP_API_URL || ''; // Use relative URL in production

      if (editingMember) {
        // Update existing member
        const response = await fetch(`${baseURL}/api/team/${editingMember.member_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(memberData)
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Update team member error:', errorText);
          try {
            const error = JSON.parse(errorText);
            throw new Error(error.detail || 'Failed to update team member');
          } catch {
            throw new Error(errorText || 'Failed to update team member');
          }
        }
        return response.json();
      } else {
        // Add new member
        const response = await fetch(`${baseURL}/api/team`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(memberData)
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Add team member error:', errorText);
          try {
            const error = JSON.parse(errorText);
            throw new Error(error.detail || 'Failed to add team member');
          } catch {
            throw new Error(errorText || 'Failed to add team member');
          }
        }
        return response.json();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['team']);
      setShowModal(false);
      setEditingMember(null);
      toast.success(editingMember ? 'Team member updated!' : 'Team member added!');
    },
    onError: (error) => {
      console.error('Save mutation error:', error);
      toast.error(error.message);
    }
  });

  // Delete team member mutation
  const deleteMutation = useMutation({
    mutationFn: async (memberId) => {
      const response = await fetch(`/api/team/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to delete team member');
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['team']);
      toast.success('Team member deleted!');
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const handleEdit = (member) => {
    setEditingMember(member);
    setShowModal(true);
  };

  const handleDelete = (member) => {
    if (window.confirm(`Are you sure you want to delete ${member.name}?`)) {
      deleteMutation.mutate(member.member_id);
    }
  };

  const handleSave = (memberData) => {
    saveMutation.mutate(memberData);
  };

  const handleCSVImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const baseURL = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${baseURL}/api/team/import-csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Failed to import CSV');

      const result = await response.json();
      queryClient.invalidateQueries(['team']);
      toast.success(`Imported ${result.imported} team members!`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <UsersIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading team</h3>
        <p className="mt-1 text-sm text-gray-500">{error.message}</p>
      </div>
    );
  }

  // Handlers for workload and projects modals
  const handleViewWorkload = (member) => {
    setSelectedMember(member);
    setShowWorkloadModal(true);
  };

  const handleViewProjects = (member) => {
    setSelectedMember(member);
    setShowProjectsModal(true);
  };

  // Filter team members
  const filteredMembers = teamMembers?.filter(member =>
    member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.role?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-gray-600">Manage your team members and roles</p>
        </div>
        <div className="flex gap-3">
          <label className="btn-secondary flex items-center gap-2 cursor-pointer">
            <ArrowUpTrayIcon className="w-4 h-4" />
            <span>Import CSV</span>
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVImport}
              className="hidden"
            />
          </label>
          <button
            onClick={() => {
              setEditingMember(null);
              setShowModal(true);
            }}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add Member</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Team Members Grid */}
      {filteredMembers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No team members found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {teamMembers?.length === 0
              ? "Get started by adding your first team member."
              : "Try adjusting your search query."}
          </p>
        </div>
      ) : (
        <>
          <div className="text-sm text-gray-600">
            Showing {filteredMembers.length} of {teamMembers?.length || 0} team members
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMembers.map((member) => (
              <TeamMemberCard
                key={member.member_id || member.id}
                member={member}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onViewWorkload={handleViewWorkload}
                onViewProjects={handleViewProjects}
              />
            ))}
          </div>
        </>
      )}

      {/* Modals */}
      {showModal && (
        <TeamMemberModal
          member={editingMember}
          onClose={() => {
            setShowModal(false);
            setEditingMember(null);
          }}
          onSave={handleSave}
        />
      )}

      {showWorkloadModal && (
        <WorkloadModal
          member={selectedMember}
          onClose={() => {
            setShowWorkloadModal(false);
            setSelectedMember(null);
          }}
        />
      )}

      {showProjectsModal && (
        <ProjectsModal
          member={selectedMember}
          onClose={() => {
            setShowProjectsModal(false);
            setSelectedMember(null);
          }}
        />
      )}
    </div>
  );
}
